import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { whitelistApi } from '../api';
import type { ReplaceWhitelistPayload, WhitelistEntry } from '../types';
import { getErrorMessage } from '../utils';

export function useWhitelist() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [items, setItems] = useState<WhitelistEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchList = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await whitelistApi.list(controller.signal);
      if (!mountedRef.current) return;
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const replace = useCallback(async (payload: ReplaceWhitelistPayload) => {
    setSaving(true);
    try {
      const result = await whitelistApi.replace(payload);
      if (mountedRef.current) {
        setItems(result.items);
        setTotalCount(result.totalCount);
      }
      return result;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchList();
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [fetchList]);

  return useMemo(
    () => ({
      items,
      totalCount,
      loading,
      saving,
      error,
      refetch: fetchList,
      replace,
    }),
    [error, fetchList, items, loading, replace, saving, totalCount]
  );
}
